import { Badge, Button, Card, Group, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';

import { metaEventsManagerPixelUrl } from '../../constants/Facebook';
import useFacebookPixelSettings from '../../helpers/useFacebookPixelSettings';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

// Event names Meta only produces via the Event Setup Tool / automatic detection;
// seeing them in the stats means the toggle is (very likely) on.
const AUTO_DETECTED_EVENTS = new Set(['SubscribedButtonClick', 'Microdata']);

// Releva's dispatcher never emits PageView, so it can't overlap.
const RELEVA_NEVER_SENT = new Set(['PageView']);

const formatLastFired = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const FacebookPixelStatus = ({ pixelId, enabledTrackingEvents = [] }) => {
  const [pixelSettings, isLoading, error] = useFacebookPixelSettings(pixelId);

  if (!pixelId) {
    return (
      <Text size="sm" c="dimmed">
        Select a Pixel in the Facebook tab to see its Meta status and the Event Setup Tool check.
      </Text>
    );
  }

  const lastFired = pixelSettings && formatLastFired(pixelSettings.lastFiredTime);
  const eventStats = pixelSettings?.eventStats;
  const hasAutoDetected = (eventStats || []).some(({ event }) => AUTO_DETECTED_EVENTS.has(event));
  const enabledSet = new Set(enabledTrackingEvents);

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text fw={500}>Pixel status</Text>

        {isLoading && <Spinner />}
        {error && <ErrorMessage message={error.message || 'Unable to load pixel status.'} />}

        {pixelSettings && (
          <Group gap="xs">
            {pixelSettings.enableAutomaticMatching ? (
              <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                Advanced Matching: on
              </Badge>
            ) : (
              <Badge color="gray" variant="light">
                Advanced Matching: off
              </Badge>
            )}
            {lastFired ? (
              <Badge color="gray" variant="light">
                Last fired: {lastFired}
              </Badge>
            ) : (
              <Badge color="yellow" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                No recent events
              </Badge>
            )}
            {pixelSettings.isUnavailable && (
              <Badge color="red" variant="light" leftSection={<IconX size={12} />}>
                Pixel unavailable
              </Badge>
            )}
            {hasAutoDetected && (
              <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                Event Setup Tool: likely ON
              </Badge>
            )}
          </Group>
        )}

        {pixelSettings && (
          <>
            <Text fw={500} size="sm">
              Events received — last 7 days
            </Text>
            {!eventStats && (
              <Text size="sm" c="dimmed">
                Event stats are unavailable for this pixel.
              </Text>
            )}
            {eventStats && eventStats.length === 0 && (
              <Text size="sm" c="dimmed">
                No events received in the last 7 days.
              </Text>
            )}
            {eventStats && eventStats.length > 0 && (
              <>
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Event</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>Browser</Table.Th>
                      <Table.Th>Server</Table.Th>
                      <Table.Th></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {eventStats.map(({ event, count, webCount, serverCount }) => (
                      <Table.Tr key={event}>
                        <Table.Td>{event}</Table.Td>
                        <Table.Td>{count == null ? '—' : count.toLocaleString()}</Table.Td>
                        <Table.Td>{webCount === null ? '—' : webCount.toLocaleString()}</Table.Td>
                        <Table.Td>{serverCount === null ? '—' : serverCount.toLocaleString()}</Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            {AUTO_DETECTED_EVENTS.has(event) && (
                              <Badge color="orange" variant="light" size="xs">
                                Auto-detected
                              </Badge>
                            )}
                            {!RELEVA_NEVER_SENT.has(event) && enabledSet.has(event) && (
                              <Badge color="yellow" variant="light" size="xs">
                                Releva sends this too
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="sm" c="dimmed">
                  Browser = pixel fires from the site; Server = Conversions API. Events marked &quot;Releva sends this
                  too&quot; will double count unless deduplicated.
                </Text>
              </>
            )}
          </>
        )}

        <Button
          component="a"
          href={metaEventsManagerPixelUrl(pixelId)}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
          rightSection={<IconExternalLink size={14} />}
        >
          Open Pixel settings in Events Manager
        </Button>
      </Stack>
    </Card>
  );
};

export default FacebookPixelStatus;
